import React, { useState } from 'react';
import { FolderOpen, Music2, Film, X, Trash2, Sparkles, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../context/LangContext';
import { useProject } from '../context/ProjectContext';
import { toast } from '../hooks/use-toast';

const ProjectPanel = () => {
  const { lang } = useLang();
  const { tracks, templates, removeTrack, addTrack, addTemplate } = useProject();
  const [open, setOpen] = useState(false);

  const total = tracks.length + templates.length;

  const clearAll = () => {
    templates.forEach(t => addTemplate(t)); // no-op due to dedup - use direct approach
    // Use localStorage direct clear
    localStorage.setItem('capcute_tracks', '[]');
    localStorage.setItem('capcute_templates', '[]');
    window.location.reload();
  };

  const removeTemplate = (id) => {
    const remaining = templates.filter(t => t.id !== id);
    localStorage.setItem('capcute_templates', JSON.stringify(remaining));
    window.dispatchEvent(new Event('storage'));
    window.location.reload();
  };

  if (total === 0 && !open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 rtl:right-auto rtl:left-5 z-40 h-12 px-5 rounded-full bg-white shadow-lg border border-neutral-200 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center gap-2"
      >
        <FolderOpen className="w-4 h-4 text-neutral-700" />
        <span className="text-[13.5px] font-bold text-neutral-900">{lang === 'sv' ? 'Mitt projekt' : 'مشروعي'}</span>
      </button>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 rtl:right-auto rtl:left-5 z-40 h-14 pl-4 pr-5 rtl:pl-5 rtl:pr-4 rounded-full bg-black text-white shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-0.5 transition-all flex items-center gap-3"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="text-left rtl:text-right">
          <div className="text-[10px] font-semibold text-white/60 uppercase tracking-wider leading-none">{lang === 'sv' ? 'Mitt projekt' : 'مشروعي'}</div>
          <div className="text-[14px] font-bold leading-tight mt-0.5">{total} {lang === 'sv' ? (total === 1 ? 'objekt' : 'objekt') : 'عنصر'}</div>
        </div>
        <ChevronUp className="w-4 h-4 text-white/70" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 rtl:right-auto rtl:left-5 z-40 w-[calc(100vw-2.5rem)] sm:w-96 bg-white rounded-3xl shadow-2xl border border-neutral-200 overflow-hidden animate-fade-up">
      <div className="p-5 border-b border-neutral-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[15px] font-black text-neutral-900">{lang === 'sv' ? 'Mitt projekt' : 'مشروعي'}</div>
            <div className="text-[11.5px] text-neutral-500">{total} {lang === 'sv' ? 'objekt sparade lokalt' : 'محفوظ محلياً'}</div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {total === 0 ? (
          <div className="p-10 text-center text-neutral-400">
            <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-[13.5px]">{lang === 'sv' ? 'Inga objekt ännu. Lägg till mallar och musik!' : 'لا توجد عناصر بعد.'}</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {templates.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Film className="w-3.5 h-3.5 text-fuchsia-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">{lang === 'sv' ? 'Mallar' : 'القوالب'}</span>
                  <span className="text-[11px] text-neutral-400">({templates.length})</span>
                </div>
                <div className="space-y-1.5">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-neutral-50 group">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-fuchsia-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <Film className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-neutral-900 truncate">{t.title}</div>
                        <div className="text-[11px] text-neutral-500">{t.category}</div>
                      </div>
                      <button onClick={() => removeTemplate(t.id)} className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-all">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tracks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Music2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">{lang === 'sv' ? 'Musikspar' : 'مقاطع موسيقية'}</span>
                  <span className="text-[11px] text-neutral-400">({tracks.length})</span>
                </div>
                <div className="space-y-1.5">
                  {tracks.map(t => (
                    <div key={t.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-neutral-50 group">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                        <Music2 className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-neutral-900 truncate">{t.title}</div>
                        <div className="text-[11px] text-neutral-500 truncate">{t.artist}</div>
                      </div>
                      <button onClick={() => { removeTrack(t.id); toast({ title: lang === 'sv' ? 'Borttaget' : 'تمت الإزالة' }); }} className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-all">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="p-4 border-t border-neutral-100 flex items-center gap-2">
          <Button className="flex-1 h-11 rounded-full bg-black hover:bg-neutral-800 text-white font-bold text-[13px]">
            {lang === 'sv' ? 'Börja redigera' : 'ابدأ التحرير'}
          </Button>
          <button onClick={clearAll} className="w-11 h-11 rounded-full bg-neutral-100 hover:bg-red-50 hover:text-red-600 text-neutral-700 flex items-center justify-center transition-colors" aria-label="clear">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectPanel;
